import { useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import {
  DatabaseBackup, CloudUpload, Download, RotateCcw, Trash2, ShieldCheck,
  AlertTriangle, CheckCircle2, Loader2, Clock, Cloud, HardDrive,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const TRIGGER_LABEL = {
  manual: "Manual",
  scheduled: "Programada",
  pre_restore: "Previa a restaurar",
};

function StatusBadge({ status }) {
  if (status === "stored") return <Badge className="bg-primary/12 text-primary border-transparent gap-1"><CheckCircle2 className="h-3 w-3" /> Guardada</Badge>;
  if (status === "creating") return <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Creando</Badge>;
  if (status === "error") return <Badge className="bg-destructive/12 text-destructive border-transparent gap-1"><AlertTriangle className="h-3 w-3" /> Error</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

export default function Backups() {
  const { data: config, loading: cfgLoading, refetch: refetchCfg } = useFetch("/admin/backups/config");
  const { data: backups, loading, refetch } = useFetch("/admin/backups");
  const [running, setRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const list = backups || [];
  const configured = config?.configured;

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/backups/run");
      toast.success("Copia de seguridad creada y subida a Google Drive");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la copia");
    } finally {
      setRunning(false);
    }
  };

  const downloadBackup = async (b) => {
    try {
      const res = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = b.filename; document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("No se pudo descargar la copia");
    }
  };

  const doRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/admin/backups/${restoreTarget.id}/restore`, { confirm: "RESTAURAR" });
      toast.success(`Datos restaurados · ${data.total_docs} registros de ${Object.keys(data.restored || {}).length} colecciones`);
      setRestoreTarget(null); setConfirmText("");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar la copia");
    } finally {
      setRestoring(false);
    }
  };

  const deleteBackup = async (b) => {
    if (!window.confirm(`¿Borrar la copia del ${fmtDate(b.created_at)}? Se eliminará también de Google Drive.`)) return;
    setDeletingId(b.id);
    try {
      await api.delete(`/admin/backups/${b.id}`);
      toast.success("Copia borrada");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar la copia");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="backups-page">
      <PageHeader title="Copias de seguridad" subtitle="Copia diaria automática de todo el sistema, guardada fuera de la app en tu Google Drive" />

      {/* Estado / configuración */}
      <Card className="border-border shadow-none p-6 max-w-3xl" data-testid="backups-status-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-navy text-white flex items-center justify-center shrink-0">
            <Cloud className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-lg">Google Drive</h3>
              {cfgLoading ? null : configured
                ? (config?.folder_reachable === false
                    ? <Badge className="bg-destructive/12 text-destructive border-transparent gap-1"><AlertTriangle className="h-3 w-3" /> Sin acceso a la carpeta</Badge>
                    : <Badge className="bg-primary/12 text-primary border-transparent gap-1"><CheckCircle2 className="h-3 w-3" /> Conectado</Badge>)
                : <Badge variant="secondary" className="font-normal">Pendiente de configurar</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Cada día se genera una copia completa (proyectos, finanzas, dining, RR.HH., usuarios y configuración)
              y se sube a una carpeta de tu Google Drive. Conservamos las <b>{config?.retention_count ?? 30}</b> copias más recientes.
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4" /> {config?.schedule_label || "Cada día de madrugada"}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><HardDrive className="h-4 w-4" /> Conservar {config?.retention_count ?? 30} copias</div>
            </div>
            {config?.environment === "preview" && (
              <div className="mt-3 rounded-lg bg-muted/60 text-muted-foreground text-xs p-3" data-testid="backups-preview-note">
                Estás en el entorno de pruebas. La copia automática diaria solo se ejecuta en producción; aquí puedes crear copias manualmente con el botón.
              </div>
            )}
            {configured && config?.folder_reachable && config?.folder_name_or_error && (
              <p className="text-xs text-muted-foreground mt-2">Carpeta de destino: <b className="text-foreground">{config.folder_name_or_error}</b></p>
            )}
            {configured && config?.folder_reachable === false && (
              <div className="mt-3 rounded-lg bg-destructive/8 text-destructive text-sm p-3">
                No puedo acceder a la carpeta de Drive. Revisa que hayas compartido la carpeta con el email de la cuenta de servicio como <b>Editor</b>. Detalle: {config.folder_name_or_error}
              </div>
            )}
          </div>
        </div>

        {!cfgLoading && !configured && (
          <div className="mt-6 rounded-lg border border-gold/30 bg-gold/[0.06] p-4 space-y-2" data-testid="backups-setup-guide">
            <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-gold-foreground dark:text-gold" /> Falta la configuración de Google Drive</div>
            <p className="text-sm text-muted-foreground">
              Para activar el envío automático, necesito dos datos que se obtienen una sola vez en Google:
              la clave de una <b>cuenta de servicio</b> y el <b>ID de la carpeta</b> de Drive donde guardar las copias.
              Pásamelos por el chat y los dejo configurados.
            </p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button onClick={runNow} disabled={running || !configured} className="gap-2" data-testid="backups-run-now-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
            {running ? "Creando copia…" : "Hacer copia ahora"}
          </Button>
          <Button onClick={() => { refetch(); refetchCfg(); }} variant="outline" className="gap-2" data-testid="backups-refresh-btn">
            <RotateCcw className="h-4 w-4" /> Actualizar
          </Button>
        </div>
      </Card>

      {/* Listado de copias */}
      <Card className="border-border shadow-none p-0 max-w-5xl overflow-hidden" data-testid="backups-list-card">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <DatabaseBackup className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Historial de copias</h3>
          <span className="text-sm text-muted-foreground">({list.length})</span>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : list.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            Todavía no hay copias. Pulsa <b>“Hacer copia ahora”</b> para crear la primera.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="px-6 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Origen</th>
                  <th className="px-4 py-3 font-medium">Contenido</th>
                  <th className="px-4 py-3 font-medium">Tamaño</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`backup-row-${b.id}`}>
                    <td className="px-6 py-3 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3"><Badge variant="secondary" className="font-normal">{TRIGGER_LABEL[b.trigger] || b.trigger}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{b.total_docs ?? 0} registros · {b.collection_count ?? 0} secciones</td>
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums">{fmtSize(b.size)}</td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" title="Descargar" disabled={!b.drive_file_id} onClick={() => downloadBackup(b)} data-testid={`backup-download-${b.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Restaurar" disabled={!b.drive_file_id} onClick={() => { setRestoreTarget(b); setConfirmText(""); }} data-testid={`backup-restore-${b.id}`}>
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Borrar" className="text-destructive hover:text-destructive" disabled={deletingId === b.id} onClick={() => deleteBackup(b)} data-testid={`backup-delete-${b.id}`}>
                          {deletingId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Diálogo de restauración (destructivo) */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) { setRestoreTarget(null); setConfirmText(""); } }}>
        <DialogContent data-testid="backup-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /> Restaurar copia de seguridad</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                Vas a restaurar la copia del <b>{fmtDate(restoreTarget?.created_at)}</b>. Esto <b>reemplazará todos los datos actuales</b> del
                sistema por los de esa copia. Se guarda automáticamente una copia del estado actual antes de restaurar, por si necesitas volver atrás.
              </span>
              <span className="block">Escribe <b>RESTAURAR</b> para confirmar:</span>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESTAURAR"
            data-testid="backup-restore-confirm-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreTarget(null); setConfirmText(""); }} data-testid="backup-restore-cancel-btn">Cancelar</Button>
            <Button
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
              disabled={confirmText !== "RESTAURAR" || restoring}
              onClick={doRestore}
              data-testid="backup-restore-confirm-btn"
            >
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {restoring ? "Restaurando…" : "Restaurar ahora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
